// ─────────────────────────────────────────────────────────────────────────────
// activityFixtures — ACTIVITY feature Part 1 (JOINT-signed v4, build step 1).
//
// The UI defines the API contract: these types ARE the response shapes that
// GET /api/rexy/activity and GET /api/rexy/activity/detail must produce in
// Part 2 (rendering core/activity_board.read_index — the SAME path the pulse
// consumes, signed). Fixture data covers every signed state so the owner
// eyeballs all of them before the backend exists.
//
// ZERO-BLEED RULE (signed H2, both walls): no conversation/transcript text
// anywhere in this module. Chat rows carry the fixed neutral subject
// "chat exchange" — NEVER transcript words. Chat detail `what` is a bounded
// neutral placeholder. 'pending' and 'unknown' rows have NO detail entry.
// ─────────────────────────────────────────────────────────────────────────────

// One index row — WHO/WHEN only, index-level (no content, ever — signed H2).
export interface ActivityRow {
  ts: string; // ISO 8601
  journal_ref: string; // 64-hex chain hash
  merged_refs?: string[]; // aggregate rows: remaining constituents' hashes (3.1)
  who: string; // agent-level; domain for boards
  domain: string;
  source_kind: 'agent_dashboard' | 'domain_board' | 'chat';
  locus: string; // WHERE — tab / board / chat key
  subject: string; // fixed neutral label for chat ("chat exchange")
  count?: number; // aggregate rows only (e.g. SECURITY ×37)
  status: 'pending' | 'read' | 'absent' | 'unknown'; // signed four-state
}

// One hour group — her real pulse slots.
export interface HourGroup {
  slot_id: string;
  label: string; // e.g. "10:00–11:00"
  pulse: {
    status: string; // "complete" | "missed" | ...
    executed_at: string | null;
    events_written: number;
  } | null; // null on the current partial hour (pulse not yet run)
  rows: ActivityRow[];
}

// GET /api/rexy/activity response shape.
export interface ActivityIndexResponse {
  success: boolean;
  window_hours: number;
  groups: HourGroup[]; // newest-first
  next_pulses: string[]; // ISO timestamps of upcoming pulse slots
}

// GET /api/rexy/activity/detail response shape — the payload atom (drill-in,
// separate authed call, 64-hex-scoped).
export interface ActivityDetail {
  journal_ref: string;
  when: string;
  who: string;
  where: string;
  what?: string;
  why?: string;
  how?: string;
  outcome?: string;
  status: 'ok' | 'absent';
}

// ── 64-hex journal refs (fake hex, realistic shape) ─────────────────────────
const REF_CHAT_TRUFFLE =
  'da84b15c449ad8202bc688461f8e37aade044156be1bdf547e9b395df2f2445f';
const REF_DASH_ARTHUR =
  '533a6368fc07c55559e90a7cd5d21a260c83a067d9ea6ba2b555564e4d417e65';
const REF_BOARD_CODE =
  'b753f109c7dab88af4d0cc373914e0c9ba074c67bf5e4bb94f6699fa4753f925';
const REF_SEC_AGG =
  '30ba007bbfc2f3420797fff0925a50a654785f00520c0442e6237abba6c05015';
const REF_SEC_MERGED_1 =
  'b07f9260e845e1498c02ae1813f7f71d150d02eeca2a46285c1b41ab22b78b28';
const REF_SEC_MERGED_2 =
  'a51ae60fad59f8165b2c2b9e0a5f54cd27b89c4122a6e8c7ce5bf30d76275f4d';
const REF_DASH_MACK_PENDING =
  '2e9d9ae33f72074b8d59a4e2659572e26b34c2d415e43a75226b7fbbc05dbcf1';
const REF_BOARD_FRONTIER_ABSENT =
  '55ba37e63045d76050b071a2e03ea4430149f7c31f2193b2fdc2b406ed17c487';
const REF_DASH_ARTHUR_UNKNOWN =
  '6d7141896d02b4af0686fb45fc2084d235fcfa8c1e00a4650b4bad788d866bfd';

// ── FIXTURE INDEX — groups newest-first, every signed state covered ─────────
export const FIXTURE_INDEX: ActivityIndexResponse = {
  success: true,
  window_hours: 12,
  groups: [
    {
      // Newest / current partial hour — pulse not yet run; carries the pending row.
      slot_id: 'slot_2026-07-21T11',
      label: '11:00–now',
      pulse: null,
      rows: [
        {
          ts: '2026-07-21T11:14:00+10:00',
          journal_ref: REF_DASH_MACK_PENDING,
          who: 'MACK',
          domain: 'TRADING',
          source_kind: 'agent_dashboard',
          locus: 'Calculator',
          subject: 'Calculator',
          status: 'pending',
        },
      ],
    },
    {
      // Complete pulse hour — all three source_kinds + the SECURITY aggregate.
      slot_id: 'slot_2026-07-21T10',
      label: '10:00–11:00',
      pulse: {
        status: 'complete',
        executed_at: '2026-07-21T11:00:00+10:00',
        events_written: 4,
      },
      rows: [
        {
          ts: '2026-07-21T10:52:00+10:00',
          journal_ref: REF_CHAT_TRUFFLE,
          who: 'TRUFFLE', // agent-level WHO (signed Ruling A)
          domain: 'PERSONAL',
          source_kind: 'chat',
          locus: 'PERSONAL_TRUFFLE_a41f',
          subject: 'chat exchange', // fixed neutral label — NEVER transcript words
          status: 'read',
        },
        {
          ts: '2026-07-21T10:31:00+10:00',
          journal_ref: REF_DASH_ARTHUR,
          who: 'ARTHUR',
          domain: 'TRADING',
          source_kind: 'agent_dashboard',
          locus: "Live Session/Today's Trades",
          subject: "Today's Trades",
          status: 'read',
        },
        {
          ts: '2026-07-21T10:22:00+10:00',
          journal_ref: REF_BOARD_CODE,
          who: 'CODE',
          domain: 'CODE',
          source_kind: 'domain_board',
          locus: 'code_proposals',
          subject: 'code_proposals',
          status: 'read',
        },
        {
          // SECURITY aggregate — chain identity per 3.1: journal_ref = newest
          // constituent's hash; merged_refs = the remaining constituents.
          ts: '2026-07-21T10:05:00+10:00',
          journal_ref: REF_SEC_AGG,
          merged_refs: [REF_SEC_MERGED_1, REF_SEC_MERGED_2],
          who: 'SECURITY',
          domain: 'SECURITY',
          source_kind: 'domain_board',
          locus: 'security_surface',
          subject: 'AUTH_REJECTED',
          count: 37,
          status: 'read',
        },
      ],
    },
    {
      // Missed pulse hour — zero rows (honest gap, no fabrication).
      slot_id: 'slot_2026-07-21T09',
      label: '09:00–10:00',
      pulse: { status: 'missed', executed_at: null, events_written: 0 },
      rows: [],
    },
    {
      // Older complete hour — FRONTIER board row (absent) + pre-schema row (unknown).
      slot_id: 'slot_2026-07-21T08',
      label: '08:00–09:00',
      pulse: {
        status: 'complete',
        executed_at: '2026-07-21T09:00:00+10:00',
        events_written: 2,
      },
      rows: [
        {
          ts: '2026-07-21T08:41:00+10:00',
          journal_ref: REF_BOARD_FRONTIER_ABSENT,
          who: 'FRONTIER',
          domain: 'FRONTIER',
          source_kind: 'domain_board',
          locus: 'frontier_board',
          subject: 'pipeline handoff',
          status: 'absent', // source unreadable at pulse — detail carries status 'absent'
        },
        {
          ts: '2026-07-21T08:17:00+10:00',
          journal_ref: REF_DASH_ARTHUR_UNKNOWN,
          who: 'ARTHUR',
          domain: 'TRADING',
          source_kind: 'agent_dashboard',
          locus: 'Live Session/Positions',
          subject: 'Positions',
          status: 'unknown', // legacy pre-schema payload — NO detail entry
        },
      ],
    },
  ],
  next_pulses: ['2026-07-21T12:00:00+10:00', '2026-07-21T13:00:00+10:00'],
};

// ── FIXTURE DETAILS — keyed by journal_ref ──────────────────────────────────
// ZERO-BLEED: no detail entries for 'pending' or 'unknown' rows; chat `what`
// is a bounded neutral placeholder; 'absent' detail carries status 'absent'.
export const FIXTURE_DETAILS: Record<string, ActivityDetail> = {
  [REF_CHAT_TRUFFLE]: {
    journal_ref: REF_CHAT_TRUFFLE,
    when: '2026-07-21T10:52:00+10:00',
    who: 'TRUFFLE',
    where: 'PERSONAL_TRUFFLE_a41f',
    what: '2-message tail read at pulse (bounded)',
    how: 'chat emitter at add_to_history choke (assistant-append)',
    outcome: 'indexed',
    status: 'ok',
  },
  [REF_DASH_ARTHUR]: {
    journal_ref: REF_DASH_ARTHUR,
    when: '2026-07-21T10:31:00+10:00',
    who: 'ARTHUR',
    where: "Live Session/Today's Trades",
    what: 'dashboard tab save',
    why: 'session data updated',
    how: 'dashboard save-path emitter (latched)',
    outcome: 'saved',
    status: 'ok',
  },
  [REF_BOARD_CODE]: {
    journal_ref: REF_BOARD_CODE,
    when: '2026-07-21T10:22:00+10:00',
    who: 'CODE',
    where: 'code_proposals',
    what: 'board write at store choke',
    how: 'board emitter at _save_proposals choke',
    outcome: 'written',
    status: 'ok',
  },
  [REF_SEC_AGG]: {
    journal_ref: REF_SEC_AGG,
    when: '2026-07-21T10:05:00+10:00',
    who: 'SECURITY',
    where: 'security_surface',
    what: 'AUTH_REJECTED aggregate — 37 events this hour',
    why: 'repeated auth rejections on the same head',
    how: 'chain projection (no double-write)',
    outcome: 'aggregated',
    status: 'ok',
  },
  [REF_BOARD_FRONTIER_ABSENT]: {
    journal_ref: REF_BOARD_FRONTIER_ABSENT,
    when: '2026-07-21T08:41:00+10:00',
    who: 'FRONTIER',
    where: 'frontier_board',
    what: 'source unreadable at pulse',
    status: 'absent',
  },
};
